import { Box, Table, TableCell, TableContainer, TableHead, TableRow, MenuItem, useTheme, Typography, Button, Popper, ClickAwayListener, Card, MenuList, TableBody, TablePagination, TextField, Checkbox, IconButton, Divider } from "@mui/material"
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useSearchParams } from 'react-router-dom'
import PropTypes from "prop-types"

// import { Icons } from '../../../utils/icons.jsx'
// import { debounce } from '../../../utils/helpers.jsx'
import SortAndFilterBar from './SortAndFilterBar'
import NoDatImg from '../../../assets/no-data.svg'
import { debounce, Icons } from '../../../utils/utilities'

const getDefaultRowStyles = ( onRowClick, theme, palette, darken, lighten ) => ( {
    "--bg-color": palette.background.default,
    cursor: Boolean( onRowClick ) && typeof onRowClick === 'function' ? "pointer" : "default",
    background: `var(--bg-color)`,
    ":hover": {
        "--bg-color": Boolean( onRowClick ) && typeof onRowClick === 'function' ? theme === "dark" ? darken( palette.text.secondary, 0.8 ) : lighten( palette.text.secondary, 0.95 ) : palette.background.default
    },

} )

// Encode 
const encodeSort = ( { field, order }, tableKey ) => `${tableKey ? `${tableKey}:` : ""}field:${field},order:${order}`
const encodeFilter = ( { field, operator, value }, tableKey ) => `${tableKey ? `${tableKey}:` : ""}field:${field},op:${operator},value:${value}`

const SortableHeader = memo( ( { setFilterRequest, setFilterOpen, col, headerFontSize, sort, filter, setSortState, hideColumn } ) => {

    const [showColOptions, setShowColOptions] = useState( false )

    const sortHeaderRef = useRef()
    const { table, zIndex, palette } = useTheme()

    const handleSortSelection = order => {
        if ( sort?.order === order ) return
        if ( !sort )
            setSortState( "set", { field: col.field, order } )
        else
            setSortState( "update", { index: sort.index, update: { field: col.field, order } } )
        setShowColOptions( false )
    }

    const handleFilterSelection = () => {
        if ( !filter )
            setFilterRequest( "set", { field: col.field, operator: "contains", value: "" } )
        else
            setFilterRequest( "remove", col.field )
        setFilterOpen( true )
        setShowColOptions( false )
    }

    const handleClick = () => {
        if ( !col.sortable ) return

        handleSortSelection( sort?.order === "asc" ? "desc" : "asc" )
    }

    const handleKeyDown = ( event ) => {
        if ( !col.sortable ) return

        const { code } = event
        if ( code === "Enter" || code === "Space" )
            handleSortSelection( sort?.order === "asc" ? "desc" : "asc" )
    }

    const removeSort = () => {
        if ( sort )
            setSortState( "remove", col.field )
        setShowColOptions( false )
    }


    return (
        <Box
            onKeyDown={handleKeyDown}
            onClick={handleClick}
            tabIndex={col.sortable ? 0 : "initial"}
            sx={{
                padding: col.padding || "5px 20px",
                cursor: col.sortable ? "pointer" : "default",
                ":hover": { background: col.sortable ? palette.action.hover : "none" }
            }}>
            <Popper sx={{ zIndex: zIndex.tooltip + 10 }} anchorEl={sortHeaderRef.current} placement='bottom-start' open={showColOptions}>
                <ClickAwayListener onClickAway={() => setShowColOptions( false )}>
                    <Box
                        sx={{
                            minWidth: "150px",
                            marginTop: "5px"
                        }}>
                        <Card>
                            <MenuList autoFocus>
                                {( !sort || sort.order !== "asc" ) && col.sortable && <MenuItem
                                    onClick={( e ) => {
                                        e.stopPropagation()
                                        handleSortSelection( "asc" )
                                    }}
                                    onKeyDown={( e ) => {
                                        if ( ["Enter", "Space"].includes( e.code ) ) {
                                            e.stopPropagation()
                                            handleSortSelection( "asc" )
                                        }
                                    }}
                                    selected={sort === "asc"}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "10px",
                                            width: "100%",
                                            justifyContent: "space-between"
                                        }}>
                                        <Typography component="span" sx={{
                                            fontSize: "14px"
                                        }} >ASC</Typography>
                                        <Typography component="span" >{Icons.default.ArrowUpward}</Typography>
                                    </Box>
                                </MenuItem>}

                                {( !sort || sort.order !== "desc" ) && col.sortable && <MenuItem
                                    onClick={( e ) => {
                                        e.stopPropagation()
                                        handleSortSelection( "desc" )
                                    }}
                                    onKeyDown={( e ) => {
                                        if ( ["Enter", "Space"].includes( e.code ) ) {
                                            e.stopPropagation()
                                            handleSortSelection( "desc" )
                                        }
                                    }}
                                    selected={sort === "desc"}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "10px",
                                            width: "100%",
                                            justifyContent: "space-between"
                                        }}>
                                        <Typography component="span" sx={{
                                            fontSize: "14px"
                                        }} >DESC</Typography>
                                        <Typography component="span" >{Icons.default.ArrowDownward}</Typography>
                                    </Box>
                                </MenuItem>}

                                {sort && col.sortable && <MenuItem
                                    onClick={e => {
                                        e.stopPropagation()
                                        removeSort()
                                    }}
                                    onKeyDown={e => {
                                        if ( ["Enter", "Space"].includes( e.code ) ) {
                                            e.stopPropagation()
                                            removeSort()
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "10px",
                                            width: "100%",
                                            alignItems: "center",
                                            justifyContent: "space-between"
                                        }}>
                                        <Typography component="span" sx={{ fontSize: "14px", }} >Remove sort</Typography>
                                        <Typography component="span" sx={{ display: "flex", alignItems: "center" }} >{Icons.default.CloseIcon}</Typography>
                                    </Box>
                                </MenuItem>}

                                {col.filterable && <Divider />}

                                {col.filterable && <MenuItem
                                    onClick={( e ) => {
                                        e.stopPropagation()
                                        handleFilterSelection()
                                    }}
                                    onKeyDown={( e ) => {
                                        if ( ["Enter", "Space"].includes( e.code ) ) {
                                            e.stopPropagation()
                                            handleFilterSelection()
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "10px",
                                            width: "100%",
                                            justifyContent: "space-between"
                                        }}>
                                        <Typography component="span" sx={{
                                            fontSize: "14px"
                                        }} >{filter ? "Remove Filter" : "Filter"}</Typography>
                                        <Typography component="span" >{filter ? Icons.default.FilterAltOff : Icons.default.FilterAlt}</Typography>
                                    </Box>
                                </MenuItem>}
                                {col.canBeHidden && <Divider />}

                                {col.canBeHidden && <MenuItem
                                    onClick={( e ) => {
                                        e.stopPropagation()
                                        hideColumn( col )
                                    }}
                                    onKeyDown={( e ) => {
                                        if ( ["Enter", "Space"].includes( e.code ) ) {
                                            e.stopPropagation()
                                            hideColumn( col )
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "10px",
                                            width: "100%",
                                            justifyContent: "space-between"
                                        }}>
                                        <Typography component="span" sx={{
                                            fontSize: "14px"
                                        }} >Hide</Typography>
                                        <Typography component="span" >{Icons.default.VisibilityOffIcon}</Typography>
                                    </Box>
                                </MenuItem>}
                            </MenuList>
                        </Card>
                    </Box>
                </ClickAwayListener>
            </Popper>
            <Box
                sx={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    flexDirection: col.align === "right" ? "row-reverse" : "row",
                    justifyContent: col.align === "center" ? "center" : "space-between"
                }}>

                <Typography noWrap variant='subtitle2' color={table.headerColor} sx={{
                    fontSize: headerFontSize
                }} >{col.name || col.label}</Typography>

                <Box
                    sx={{
                        display: "flex",
                        gap: "0",
                        marginRight: "-10px",
                        alignItems: "center",
                        fontSize: 'inherit',
                        flexDirection: col.align === "right" ? "row-reverse" : "row",
                        justifyContent: col.align === "center" ? "center" : col.align === "left" ? "flex-start" : "flex-end"
                    }}>
                    {filter && <IconButton onClick={() => setFilterOpen( true )} size='small' >{Icons.default.FilterAlt}</IconButton>}
                    {col.sortable && <IconButton disabled size='small' >{!sort ? Icons.default.Sort : sort.order === "asc" ? Icons.default.ArrowUpward : Icons.default.ArrowDownward}</IconButton>}
                    <IconButton ref={sortHeaderRef} onClick={event => {
                        event.stopPropagation()
                        setShowColOptions( true )
                    }} size='small'>
                        {Icons.default.MoreVert}
                    </IconButton>
                </Box>
            </Box>
        </Box>
    )
} )


/**
 * CustomTable
 *
 * A fully client-side, feature-rich table component built with MUI. Features include:
 * - Global search (debounced, URL-synced)
 * - Multi-column sorting (persisted to URL)
 * - Per-column filtering with configurable operators (persisted to URL)
 * - Pagination (client-side)
 * - Optional per-column link rendering and cell click handlers
 * - Column hiding (initial hides + toggleable)
 * - Custom action buttons that receive the currently processed rows
 *
 * URL state (via react-router useSearchParams):
 * - "globalQueries" = string (global search query)
 * - "sort" = repeated param; encoded as "field:<field>,order:<asc|desc>"
 * - "filter" = repeated param; encoded as "field:<field>,op:<operator>,value:<value>"
 * - "filtercondition" = "and" | "or" (how multiple filters combine; default "and")
 *
 * Notes on behavior:
 * - Global search is debounced (500ms) and written to "globalQueries" URL param.
 * - Sort operations are multi-column and applied in the sequence they appear in the URL.
 * - Sorting uses a column's custom "comparator" when provided. Otherwise a default comparator is used:
 *   - If `renderValue` exists on a column it is used to derive the comparison value for that column.
 *   - Null/undefined are sorted consistently.
 *   - Numeric comparisons are used if values are numbers or the column declares dataType: 'number'.
 *   - Strings are compared case-insensitively.
 * - Per-column filters support operators: contains, eq, neq, startswith, endswith.
 * - Filter logic can be "and" (every filter must pass) or "or" (any filter may pass).
 * - Pagination is client-side and slices the processed (searched/filtered/sorted) rows.
 * - Columns can be hidden initially via the `hide` flag; users may toggle hide via the header menu if `canBeHidden`.
 *
 * Props:
 * @param {boolean|string} [globalSearch=false] - Enable global search; can be a placeholder string if truthy.
 * @param {Array<Object>} [customActions=[]] - Custom action buttons rendered in the toolbar. Each action:
 *   - {string} type - 'button' currently supported
 *   - {React.Node} [label] - Button label
 *   - {Function} [onClick] - Called with processedRows (after search/filter/sort)
 *   - {Object} [buttonStyles] - sx styles for the button
 *   - {string} [color] - color key for the MUI Button
 *   - {React.Node} [startIcon], [endIcon] - icons for the button
 * @param {Function|import('@mui/system').SxProps} [rowStyles] - Optional callback invoked with the row object when a whole row is clicked
 * @param {import('@mui/system').SxProps} [tableStyles={}] - Styles applied to the TableContainer (sx)
 * @param {string|number} [dataIdKey] - Optional unique key field name (or numeric) in each row used as React key; fallback to index if absent
 * @param {Function} [onRowClick] - Optional callback invoked with the row object when a whole row is clicked
 * @param {string} [headerTextTransform='capitalize'] - CSS text-transform for header labels
 * @param {string|number} [headerFontSize='14px'] - Font size for header labels
 * @param {Array<Object>} columns - Column definitions (required). Each column accepts:
 *   - {string} field - unique field name (required)
 *   - {string} [name] / {string} [label] - header display text
 *   - {number|string} [width] - minWidth used for header cell
 *   - {number|string} [maxWidth]
 *   - {boolean} [sortable=false] - enables sort menu for this column
 *   - {boolean} [filterable=false] - include this column in per-column filters
 *   - {Function} [renderCell] - (row) => ReactNode to render cell content
 *   - {Function} [renderValue] - (row) => primitive used for sorting/filtering (preferred over raw row[field])
 *   - {Function} [comparator] - (a, b) => number custom comparator when sorting by this column
 *   - {boolean} [hide=false] - hide by default
 *   - {boolean} [canBeHidden=false] - show "Hide" option in header menu
 *   - {'left'|'right'|'center'} [align='left'] - alignment for the cell
 *   - {string} [padding] - header padding
 *   - {string} [rowPadding] - cell padding for body cells
 *   - {Object} [styles] - header cell styles (sx)
 *   - {Object} [cellStyles] - body cell styles (sx)
 *   - {Array<any>} [filterOptions] - optional list of permitted filter values (UI may use this)
 *   - {Function} [linkGetter] - (row) => string path to be wrapped with <Link> for this cell
 *   - {Function} [onCellClick] - (row) => void invoked when the cell is clicked
 *   - {boolean} [index=false] - if true, cell renders row index (1-based for page)
 * @param {Array<Object>} data - Array of row objects to render (required)
 * @param {number} [pageSize=50] - Default number of rows per page
 * @param {Object} [initialSort=null] - Optional initial sort descriptor; supported keys:
 *   - {string} field
 *   - {string} order | {string} direction - 'asc' | 'desc'
 *   (Note: component encodes sort entries to the "sort" URL param)
 * @param {boolean} [enableColumnFilters=true] - If false, disables column filtering features in the UI
 * @param {import('@mui/system').SxProps} [containerStyles={}] - Outer container styles (sx)
 * @param {string|Object} [headerBg] - Header background color or sx object override
 *
 * Return:
 * @returns {JSX.Element} A MUI-based table element with toolbar, sticky header, body rows, and pagination.
 *
 * Example:
 * const columns = [
 *   { field: 'id', name: 'ID', sortable: true, align: 'left' },
 *   { field: 'name', name: 'Name', sortable: true, filterable: true, renderCell: r => r.name }
 * ]
 * <CustomTable columns={columns} data={rows} pageSize={25} globalSearch />
 */
export default function CustomTable ( { rowStyles, selectableRows = false, selected = [], setSelected, onFilterRemove, hasFilters = false, globalSearch = false, customActions = [], tableStyles = {}, dataIdKey, onRowClick, headerTextTransform = "capitalize", headerFontSize = "14px", columns, data, pageSize = 50, initialSort = null, enableColumnFilters = true, containerStyles = {}, headerBg, tableKey = "", noDataBlock, className = "", tableBg } ) {

    const [hiddenColumns, setHiddenColumns] = useState( columns.filter( col => col.hide ).map( col => col.field ) )
    const [showingStickyColumnsShadows, setShowingStickyColumnsShadows] = useState( { left: false, right: false, top: false, bottom: false } )
    const [rowsPerPage, setRowsPerPage] = useState( pageSize )

    const [page, setPage] = useState( 0 )

    const [sortOpen, setSortOpen] = useState( false )
    const [filterOpen, setFilterOpen] = useState( false )

    const { theme, palette, layout, table, border, lighten, darken } = useTheme()
    const tableContainerRef = useRef()

    const [searchParams, setSearchParams] = useSearchParams( { filtercondition: "and" } )

    const normalizedHeaderBg = headerBg || palette.background.default

    const isAllSelected = data.length > 0 && selected.length === data.length
    const isIndeterminate = selected.length > 0 && selected.length < data.length

    const handleSelectAll = ( event ) => {
        if ( event.target.checked ) {
            setSelected( processedRows.map( row => row[dataIdKey] ) )
        } else {
            setSelected( [] )
        }
    }

    const handleRowSelect = ( id ) => {
        const currentIndex = selected.indexOf( id )
        const newSelected = [...selected]

        if ( currentIndex === -1 ) {
            newSelected.push( id )
        } else {
            newSelected.splice( currentIndex, 1 )
        }
        setSelected( newSelected )
    }

    // Filter and Sort Value Getters
    const globalQueries = useMemo( () => {
        return searchParams.getAll( 'globalQueries' ).map( item => {
            if ( tableKey && item.startsWith( `${tableKey}:` ) ) {
                const [, searchTerm] = item.split( `${tableKey}:` )
                return { key: tableKey, searchTerm }
            }
            return { searchTerm: item }
        } )
    }, [searchParams, tableKey] )

    // 2. Use lazy initialization and optional chaining to safely set initial state once
    const [globalQueryFieldValue, setGlobalQueryFieldValue] = useState( () => {
        const match = globalQueries.find( i => tableKey ? i.key === tableKey : !i.key )
        return match?.searchTerm ?? ''
    } )
    const filtercondition = searchParams.get( 'filtercondition' ) || 'and'

    const sorts = searchParams.getAll( "sort" ).filter( item => tableKey ? item.startsWith( tableKey ) : true ).map( item => {
        let sortDetails = []
        if ( tableKey ) {
            sortDetails = item.split( `${tableKey}:` ).at( 1 ).split( "," ).map( p => p.split( ":" ) )
        } else {
            sortDetails = item.split( "," ).map( p => p.split( ":" ) )
        }
        const obj = Object.fromEntries( sortDetails )
        return { field: obj.field, order: obj.order }
    } )

    const filters = searchParams.getAll( "filter" ).filter( item => tableKey ? item.startsWith( tableKey ) : true ).map( item => {
        let filterDetails = []
        if ( tableKey ) {
            filterDetails = item.split( `${tableKey}:` ).at( 1 ).split( "," ).map( p => p.split( ":" ) )
        } else {
            filterDetails = item.split( "," ).map( p => p.split( ":" ) )
        }

        const obj = Object.fromEntries( filterDetails )
        return { field: obj.field, operator: obj.op, value: obj.value }
    } )

    const [sortColumnMap, setSortColumnMap] = useState( () => {
        const sortMap = {}
        for ( const [i, s] of sorts.entries() ) {
            sortMap[s.field] = { order: s.order, index: i }
        }
        return sortMap
    } )
    const [filterColumnMap, setFilterColumnMap] = useState( () => {
        const filterMap = {}
        for ( const s of filters ) {
            filterMap[s.field] = s
        }
        return filterMap
    } )

    const filterables = useMemo( () => columns.filter( col => col.filterable ), [columns] )
    const sortables = useMemo( () => columns.filter( col => col.sortable ), [columns] )
    const searchables = useMemo( () => columns.filter( col => col.searchable ), [columns] )

    const toggleHideColumn = ( column ) => {
        const columnIndex = hiddenColumns.findIndex( col => col === column.field )
        if ( columnIndex >= 0 ) {
            setHiddenColumns( prev => prev.filter( col => col !== column.field ) )
        } else {
            setHiddenColumns( prev => [...prev, column.field] )
        }

    }

    const debouncesGlobalQuerySetter = debounce( 'table-global-search', ( value ) => {

        const newQueries = [...globalQueries]

        try {

            if ( tableKey ) {
                if ( value?.trim() ) {
                    const searchQuery = newQueries.find( item => item.key === tableKey )
                    if ( searchQuery )
                        searchQuery.searchTerm = value
                    else
                        newQueries.push( { searchTerm: value, key: tableKey } )
                    console.log( newQueries )

                } else {
                    const index = newQueries.findIndex( item => item.key === tableKey )
                    if ( index >= 0 )
                        newQueries.splice( index, 1 )
                }
            } else {
                if ( value?.trim() ) {
                    const searchQuery = newQueries.find( item => !item.key )
                    if ( searchQuery )
                        searchQuery.searchTerm = value
                    else
                        newQueries.push( { searchTerm: value } )
                } else {
                    const index = newQueries.findIndex( item => !item.key )
                    if ( index >= 0 )
                        newQueries.splice( index, 1 )
                }
            }

            setSearchParams( prev => {
                const params = new URLSearchParams( prev )
                params.delete( "globalQueries" )
                newQueries.forEach( s => params.append( "globalQueries", s.key ? `${s.key}:${s.searchTerm}` : s.searchTerm ) )
                return params
            }, { replace: true } )
        } catch ( err ) {
            console.log( err )
        }

    }, 500 )

    const handleGlobalSearchChange = ( event ) => {
        setGlobalQueryFieldValue( event.target.value )
        debouncesGlobalQuerySetter( event.target.value || "" )
    }

    const handleSortRequest = ( action = "set", data ) => {
        let newSort = [...sorts]

        if ( action === "set" ) {
            newSort.push( data )
        } else if ( action === "remove" ) {
            newSort = newSort.filter( s => s.field !== data )
        } else if ( action === "update" ) {
            const { index, update } = data
            const value = { ...newSort[index], ...update }
            newSort.splice( index, 1, value )
        }

        // build sortColumnMap
        const sortColumnMapValue = {}
        for ( const [i, s] of newSort.entries() ) {
            sortColumnMapValue[s.field] = { order: s.order, index: i }
        }
        setSortColumnMap( sortColumnMapValue )

        // update URL
        setSearchParams( prev => {
            const params = new URLSearchParams( prev )
            params.delete( "sort" ) // clear old entries
            newSort.forEach( s => params.append( "sort", encodeSort( s, tableKey ) ) )
            return params
        }, { replace: true } )
    }

    const handleFilterRequest = ( action = "set", data ) => {
        let newFilter = [...filters]

        if ( action === "set" ) {
            newFilter.push( data )
        } else if ( action === "remove" ) {
            newFilter = newFilter.filter( f => f.field !== data )
        } else if ( action === "update" ) {
            const { index, update } = data
            const value = { ...newFilter[index], ...update }
            newFilter.splice( index, 1, value )
        }

        // build sortColumnMap
        const filterColumnMap = {}
        for ( const s of newFilter ) {
            filterColumnMap[s.field] = s
        }

        setFilterColumnMap( filterColumnMap )

        setSearchParams( prev => {
            const params = new URLSearchParams( prev )
            params.delete( "filter" ) // clear old entries
            newFilter.forEach( f => params.append( "filter", encodeFilter( f, tableKey ) ) )
            return params
        }, { replace: true } )
    }

    const handleChangePage = ( _, newPage ) => {
        setPage( newPage )
    }

    const handleChangeRowsPerPage = ( event ) => {
        setRowsPerPage( parseInt( event.target.value, 10 ) )
        setPage( 0 )
    }

    const handleTableScroll = debounce( "tableScrollWatcher", event => {
        const { scrollWidth, scrollLeft, clientWidth, scrollHeight, scrollTop, clientHeight } = event.target
        const states = {}
        states.left = scrollLeft > 0
        states.right = scrollLeft < scrollWidth - clientWidth

        states.top = scrollTop > 0
        states.bottom = scrollTop < scrollHeight - clientHeight

        setShowingStickyColumnsShadows( states )
    }, 250 )

    const removeFilters = () => {
        setSearchParams( ( prev ) => {
            const params = new URLSearchParams( prev )
            params.delete( 'filter' )
            params.delete( 'selectedDepartment' )
            params.delete( 'selectedInstitute' )
            params.delete( 'filtercondition' )
            return params
        }, { replace: true } )
        setFilterColumnMap( {} )
        if ( onFilterRemove && typeof onFilterRemove === 'function' )
            onFilterRemove()
    }

    // comparator helper
    const getComparator = useCallback( ( field, dir ) => {
        const col = columns.find( ( c ) => c.field === field )
        const multiplier = dir === "asc" ? 1 : -1
        if ( !col ) return () => 0
        if ( typeof col.comparator === "function" ) {
            return ( a, b ) => {
                const compare = col.comparator( a, b )
                return multiplier * compare
            }
        }

        const getVal = ( item ) => {
            if ( typeof col.renderValue === 'function' ) {
                return col.renderValue( item )
            }
            if ( typeof col.renderCell === 'function' ) {
                const cell = col.renderCell( item )
                if ( typeof cell === 'string' || typeof cell === 'number' ) {
                    return cell
                }
            }
            return item[col.field]
        }

        // default comparator
        return ( a, b ) => {
            const va = getVal( a )
            const vb = getVal( b )

            if ( va == null && vb == null ) return 0
            if ( va == null ) return -1 * multiplier
            if ( vb == null ) return 1 * multiplier
            if ( col.dataType === 'number' || ( typeof va === "number" && typeof vb === "number" ) ) return ( va - vb ) * multiplier
            const sa = String( va ).toLowerCase()
            const sb = String( vb ).toLowerCase()

            if ( sa < sb ) return -1 * multiplier
            if ( sa > sb ) return 1 * multiplier
            return 0
        }
    }, [columns] )

    const processedRows = useMemo( () => {
        let rows = [...data]

        // 1. Global search
        if ( globalQueries.find( item => tableKey ? item.key === tableKey : !item.key ) ) {
            let searchTerm = ""
            if ( tableKey ) {
                const searchQuery = globalQueries.find( item => item.key === tableKey )
                searchTerm = searchQuery?.searchTerm || ""
            } else {
                const searchQuery = globalQueries.find( item => !item.key )
                searchTerm = searchQuery?.searchTerm || ""
            }
            if ( searchTerm.trim() ) {
                const q = searchTerm.trim().toLowerCase()
                if ( searchables.length === 0 ) {
                    rows = rows.filter( row => {
                        return columns.some( col => {
                            const cellValue = String( col.renderValue && typeof col.renderValue === 'function' ? col.renderValue( row ) : col.renderCell && typeof col.renderCell === 'function' && typeof col.renderCell( row ) === 'string' ? col.renderCell( row ) : row[col.field] || "" ).toLowerCase()
                            if ( cellValue.includes( q ) ) return true
                            return false
                        } )
                    }
                    )
                } else {
                    rows = rows.filter( row => {
                        return searchables.some( col => {
                            const cellValue = String( col.renderValue && typeof col.renderValue === 'function' ? col.renderValue( row ) : col.renderCell && typeof col.renderCell === 'function' && typeof col.renderCell( row ) === 'string' ? col.renderCell( row ) : row[col.field] || "" ).toLowerCase()
                            if ( cellValue.includes( q ) ) return true
                            return false
                        } )
                    }
                    )
                }
            }
        }

        const fieldAndColumnsMap = {}
        for ( const filterable of filterables ) {
            fieldAndColumnsMap[filterable.field] = filterable
        }

        // 2. Per-column filters
        rows = rows.filter( row => {

            const filterHandler = ( { field, value, operator } ) => {

                if ( !value ) return true // no filter
                const col = fieldAndColumnsMap[field]
                let cell = String( col.renderValue && typeof col.renderValue === 'function' ? col.renderValue( row ) : col.renderCell && typeof col.renderCell === 'function' ? col.renderCell( row ) : row[col.field] || "" ).toLowerCase()

                let filterValue = String( value ).toLowerCase()
                if ( col.dataType === "number" ) {
                    cell = +cell
                    filterValue = +filterValue
                }

                switch ( operator ) {
                    case "contains":
                        return cell.includes( filterValue )
                    case "eq":
                        return cell === filterValue
                    case "neq":
                        return cell !== filterValue
                    case "startswith":
                        return cell.startsWith( filterValue )
                    case "endswith":
                        return cell.endsWith( filterValue )
                    case "gt":
                        return cell > filterValue
                    case "lt":
                        return cell < filterValue
                    default:
                        return true
                }
            }

            if ( filtercondition === "or" )
                return filters.some( filterHandler )
            else
                return filters.every( filterHandler )
        } )

        // 3. Multi-column sort
        const sortArray = sorts
        if ( Array.isArray( sortArray ) && sortArray.length > 0 ) {
            const comparators = sortArray.map( s => getComparator( s.field, s.order ) )

            rows.sort( ( a, b ) => {
                for ( const comparator of comparators ) {
                    const compare = comparator( a, b )
                    if ( compare !== 0 ) return compare
                }
                return 0
            } )
        }

        return rows
    }, [data, globalQueries, filters, sorts, filterables, filtercondition, getComparator, searchables, columns, tableKey] )

    // slice for pagination
    const visibleRows = useMemo( () => {
        const start = page * rowsPerPage
        return processedRows.slice( start, start + rowsPerPage )
    }, [processedRows, page, rowsPerPage] )

    useEffect( () => {
        const start = page * rowsPerPage
        if ( start >= processedRows.length )
            setPage( 0 )
    }, [processedRows, page, rowsPerPage] )

    useEffect( () => {
        if ( !tableContainerRef.current ) return
        const { scrollWidth, scrollLeft, clientWidth, scrollHeight, scrollTop, clientHeight } = tableContainerRef.current
        const states = {}
        states.left = scrollLeft > 0
        states.right = scrollLeft < scrollWidth - clientWidth

        states.top = scrollTop > 0
        states.bottom = scrollTop < scrollHeight - clientHeight

        setShowingStickyColumnsShadows( states )
    }, [] )

    return (
        <Box className={className} sx={{ width: "100%", flexGrow: 1, height: "100%", display: "flex", border: border[1], borderRadius: layout.borderRadius, flexDirection: "column", overflow: "auto", minHeight: "0", ...containerStyles }}>
            {( sortables.length > 0 || filterables.length > 0 || customActions.length > 0 || globalSearch ) && <Box
                sx={{
                    display: "flex",
                    padding: "10px",
                    gap: layout.cardsGap,
                    justifyContent: "space-between",
                    flexWrap: "wrap"
                }}>
                {( sortables.length > 0 || filterables.length > 0 ) && <SortAndFilterBar tableKey={tableKey} removeFilters={removeFilters} sortOpen={sortOpen} filterOpen={filterOpen} setFilterOpen={setFilterOpen} setSortOpen={setSortOpen} sorts={sorts} sortables={sortables} filterables={filterables} onSortChange={handleSortRequest} onFilterChange={handleFilterRequest} filters={filters} />}

                {globalSearch && <TextField sx={{ flexGrow: 1 }} slotProps={{ input: { sx: { borderRadius: layout.borderRadius } } }} size='small' placeholder={typeof globalSearch === 'string' ? globalSearch : "Search table"} value={globalQueryFieldValue} onChange={handleGlobalSearchChange} />}

                {customActions.length > 0 && <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: layout.cardsGap
                    }}>
                    {customActions.map( ( action, actionIndex ) => {
                        if ( action.type === 'button' && action.visible !== false ) {
                            return ( <Button
                                key={actionIndex}
                                onClick={() => {
                                    if ( typeof action.onClick === 'function' )
                                        action.onClick( processedRows )
                                }}
                                sx={{ ...( action.buttonStyles || {} ) }}
                                size='small'
                                variant={action.variant || 'outlined'}
                                color={action.color || 'greyed'}
                                startIcon={action.startIcon}
                                endIcon={action.endIcon}
                            >
                                {action.label}
                            </Button> )
                        } else return ''
                    } )}
                </Box>}
            </Box>}
            <Box component={TableContainer} ref={tableContainerRef} onScroll={handleTableScroll} className='slim-custom-scrollbar' sx={{ overflow: "auto", flexGrow: 1, ...tableStyles }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {selectableRows && (
                                <TableCell
                                    align="center"
                                    sx={{
                                        padding: "0",
                                        borderBottom: border[1],
                                        background: normalizedHeaderBg
                                    }}>
                                    <Checkbox
                                        indeterminate={isIndeterminate}
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                        size="small"
                                    />
                                </TableCell>
                            )}
                            {columns
                                .filter( ( c ) => !c.hide && !hiddenColumns.includes( c.field ) )
                                .map( ( col, colIndex ) => (
                                    <TableCell
                                        key={colIndex}
                                        align={col.align || 'left'}
                                        sx={{
                                            fontSize: headerFontSize,
                                            textTransform: headerTextTransform,
                                            color: table.headerColor,
                                            padding: "0",
                                            maxWidth: col.maxWidth,
                                            minWidth: col.width,
                                            borderBottom: border[1],
                                            background: normalizedHeaderBg,
                                            "::after": {
                                                borderRight: `2px solid ${palette.divider}`,
                                                content: '""',
                                                position: "absolute",
                                                width: "1px",
                                                right: 0,
                                                height: "60%",
                                                top: "20%",
                                            },
                                            "&:last-child::after": {
                                                display: "none"
                                            },
                                            left: col.stickTo === "left" ? 0 : "initial",
                                            right: col.stickTo === "right" ? 0 : "initial",
                                            zIndex: ["left", "right"].includes( col.stickTo ) ? 10 : 1,
                                            "::before": {
                                                content: "''",
                                                width: "20px",
                                                top: 0,
                                                display: showingStickyColumnsShadows[col.stickTo] ? "block" : "none",
                                                height: "100%",
                                                position: "absolute",
                                                background: `linear-gradient(to ${col.stickTo === "left" ? "right" : "left"}, ${normalizedHeaderBg}, transparent 100%)`,
                                                left: col.stickTo === "left" ? "100%" : "initial",
                                                right: col.stickTo === "right" ? "100%" : "initial",
                                            },
                                            ...( col.styles || {} ),
                                        }}
                                    >
                                        {( col.sortable === true || col.canBeHidden === true || col.filterable === true ) ?
                                            <SortableHeader setFilterOpen={setFilterOpen} setFilterRequest={handleFilterRequest} hideColumn={toggleHideColumn} headerFontSize={headerFontSize} setSortState={handleSortRequest} col={col} filter={filterColumnMap[col.field]} sort={sortColumnMap[col.field]} /> : (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        padding: col.padding || "10px 20px",
                                                        justifyContent: col.align ? col.align === "left" ? "flex-start" : col.align === "right" ? "flex-end" : col.align : "left",
                                                        gap: "2px",
                                                        alignItems: "center",
                                                        fontSize: 'inherit',
                                                        overflow: "hidden"
                                                    }}>
                                                    <Typography sx={{ fontSize: "inherit", fontWeight: "inherit", minWidth: 0, overflow: "hidden" }} className={( col.wrap ?? true ) ? "truncate-at-1" : ""} noWrap={col.wrap ?? true}>{col.name || col.label}</Typography>
                                                    {filterColumnMap[col.field] && <Typography
                                                        component="span"
                                                        color='textSecondary'
                                                        sx={{
                                                            marginLeft: "10px",
                                                            display: "flex",
                                                            gap: "2px",
                                                            alignItems: "center",
                                                            fontSize: "inherit",
                                                            height: "fit-content"
                                                        }}>
                                                        {filterColumnMap[col.field] && Icons.default.FilterAlt}
                                                    </Typography>}
                                                </Box>
                                            )}
                                    </TableCell>
                                ) )}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {visibleRows.map( ( row, idx ) => {
                            const key = dataIdKey ? row?.[dataIdKey] ?? idx : idx

                            const normalizedStyles = rowStyles ? typeof rowStyles === "function" ? { ...getDefaultRowStyles( onRowClick, theme, palette, darken, lighten ), ...rowStyles( row ) } : rowStyles : getDefaultRowStyles( onRowClick, theme, palette, darken, lighten )

                            return (
                                <TableRow
                                    className='table-row'
                                    sx={normalizedStyles}
                                    onClick={Boolean( onRowClick ) && typeof onRowClick === 'function' ? ( event ) => onRowClick( row, idx, event ) : undefined}
                                    tabIndex={0}
                                    key={key}
                                >
                                    {selectableRows && (
                                        <TableCell padding="checkbox" sx={{ padding: "0" }}>
                                            <Checkbox
                                                checked={selected.includes( key )}
                                                onClick={( e ) => {
                                                    e.stopPropagation()
                                                    handleRowSelect( key )
                                                }}
                                                size="small"
                                            />
                                        </TableCell>
                                    )}
                                    {columns
                                        .filter( ( c ) => !c.hide && !hiddenColumns.includes( c.field ) )
                                        .map( ( col ) => {
                                            if ( !col.linkGetter && typeof col.linkGetter !== 'function' ) {
                                                return (
                                                    <TableCell onClick={() => col?.onCellClick?.( row )}
                                                        sx={{
                                                            color: table.color, padding: col.rowPadding || "5px 20px", cursor: col?.onCellClick ? "pointer" : "inherit", left: col.stickTo === "left" ? 0 : "initial",
                                                            right: col.stickTo === "right" ? 0 : "initial",
                                                            zIndex: ["left", "right"].includes( col.stickTo ) ? 9 : 1,
                                                            borderBottom: `1px solid ${palette.borderColor}`,
                                                            position: ["left", "right"].includes( col.stickTo ) ? "sticky" : "initial",
                                                            background: "inherit",
                                                            "::before": {
                                                                content: "''",
                                                                width: "20px",
                                                                top: 0,
                                                                display: showingStickyColumnsShadows[col.stickTo] ? "block" : "none",
                                                                height: "100%",
                                                                position: "absolute",
                                                                background: `linear-gradient(to ${col.stickTo === "left" ? "right" : "left"}, var(--bg-color), transparent 100%)`,
                                                                left: col.stickTo === "left" ? "100%" : "initial",
                                                                right: col.stickTo === "right" ? "100%" : "initial",
                                                            },
                                                            ...( col.cellStyles || {} )
                                                        }} key={col.field} align={col.align ?? "left"}>
                                                        {col.index ? ( ( page * rowsPerPage ) + ( idx + 1 ) ) : typeof col.renderCell === "function" ? col.renderCell( row, idx ) : row[col.field] || ( col.dataType === "number" ? 0 : "-" )}
                                                    </TableCell>
                                                )
                                            } else {
                                                return (
                                                    <TableCell sx={{
                                                        width: col.width, maxWidth: col.maxWidth, color: table.color, padding: "0", left: col.stickTo === "left" ? 0 : "initial",
                                                        right: col.stickTo === "right" ? 0 : "initial",
                                                        zIndex: ["left", "right"].includes( col.stickTo ) ? 9 : 1,
                                                        background: "inherit",
                                                        borderBottom: `1px solid ${palette.borderColor}`,
                                                        position: ["left", "right"].includes( col.stickTo ) ? "sticky" : "initial",
                                                        ":hover": { textDecoration: "underline" },
                                                        "::before": {
                                                            content: "''",
                                                            width: "20px",
                                                            top: 0,
                                                            display: showingStickyColumnsShadows[col.stickTo] ? "block" : "none",
                                                            height: "100%",
                                                            position: "absolute",
                                                            background: `linear-gradient(to ${col.stickTo === "left" ? "right" : "left"}, var(--bg-color), transparent 100%)`,
                                                            left: col.stickTo === "left" ? "100%" : "initial",
                                                            right: col.stickTo === "right" ? "100%" : "initial",
                                                        },
                                                        ...( col.cellStyles || {} )
                                                    }} key={col.field} align={col.align ?? "left"}>
                                                        <Link to={col.linkGetter( row )} className='default-link'>
                                                            <Typography
                                                                component="div"
                                                                sx={{
                                                                    height: "max-content",
                                                                    fontSize: table.fontSize || "14px",
                                                                    padding: col.rowPadding || "5px 20px",
                                                                    color: table.color,
                                                                    ...( col.cellStyles || {} )
                                                                }}>
                                                                {col.index ? ( idx + 1 ) : typeof col.renderCell === "function" ? col.renderCell( row, idx ) : row[col.field] || ( col.dataType === "number" ? 0 : "-" )}
                                                            </Typography>
                                                        </Link>
                                                    </TableCell>
                                                )
                                            }
                                        } )}
                                </TableRow>
                            )
                        } )}

                        {visibleRows.length === 0 && <TableRow>
                            <TableCell colSpan={columns.filter( ( c ) => !c.hide ).length} align="center">
                                {noDataBlock ?
                                    ( <TableCell colSpan={columns.filter( ( c ) => !c.hide ).length} align="center">{noDataBlock}</TableCell> )
                                    : ( <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: "10px",
                                            padding: "10px"
                                        }}>
                                        <Box component="img" src={NoDatImg} alt='No records to show' sx={{
                                            width: "150px"
                                        }} />
                                        <Typography variant='subtitle2' sx={{
                                            textAlign: "center"
                                        }}>No results {( filters.length > 0 || hasFilters ) && "for the applied filter"}</Typography>
                                        {( filters.length > 0 || hasFilters ) && <Button variant='contained' color='error' disableElevation size="small" onClick={removeFilters} startIcon={Icons.default.FilterAltOff}>
                                            Remove Filters
                                        </Button>}
                                    </Box> )}
                            </TableCell>
                        </TableRow>
                        }
                    </TableBody>
                </Table>
            </Box>
            {rowsPerPage && <TablePagination
                slotProps={{
                    select: {
                        sx: { borderRadius: 5 },
                        MenuProps: {
                            elevation: 1,
                            anchorOrigin: {
                                vertical: "top",
                                horizontal: "center"
                            },
                            transformOrigin: {
                                vertical: "bottom",
                                horizontal: "center"
                            }
                        }
                    }
                }}
                rowsPerPageOptions={[50, 100, 200, 500]}
                sx={{ flexShrink: 0, bgcolor: palette.contentBg }}
                component="div"
                count={processedRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />}
        </Box >
    )
}

CustomTable.propTypes = {
    globalSearch: PropTypes.oneOfType( [PropTypes.bool, PropTypes.string] ),
    customActions: PropTypes.arrayOf(
        PropTypes.shape( {
            type: PropTypes.string.isRequired,
            label: PropTypes.node,
            onClick: PropTypes.func,
            buttonStyles: PropTypes.object,
            color: PropTypes.string,
            startIcon: PropTypes.node,
            endIcon: PropTypes.node,
        } )
    ),
    tableStyles: PropTypes.object,
    dataIdKey: PropTypes.oneOfType( [PropTypes.string, PropTypes.number] ),
    onRowClick: PropTypes.func,
    headerTextTransform: PropTypes.string,
    headerFontSize: PropTypes.oneOfType( [PropTypes.string, PropTypes.number] ),
    columns: PropTypes.arrayOf(
        PropTypes.shape( {
            field: PropTypes.string.isRequired,
            name: PropTypes.string,
            label: PropTypes.string,
            width: PropTypes.oneOfType( [PropTypes.number, PropTypes.string] ),
            maxWidth: PropTypes.oneOfType( [PropTypes.number, PropTypes.string] ),
            sortable: PropTypes.bool,
            filterable: PropTypes.bool,
            renderCell: PropTypes.func,
            renderValue: PropTypes.func,
            comparator: PropTypes.func,
            hide: PropTypes.bool,
            canBeHidden: PropTypes.bool,
            align: PropTypes.oneOf( ["left", "right", "center"] ),
            padding: PropTypes.string,
            rowPadding: PropTypes.string,
            styles: PropTypes.object,
            cellStyles: PropTypes.object,
            filterOptions: PropTypes.array,
            linkGetter: PropTypes.func,
            onCellClick: PropTypes.func,
            index: PropTypes.bool,
        } )
    ).isRequired,
    data: PropTypes.arrayOf( PropTypes.object ).isRequired,
    pageSize: PropTypes.number,
    initialSort: PropTypes.shape( {
        field: PropTypes.string,
        // component uses "order" for URL encoding but older docs may use "direction"
        order: PropTypes.oneOf( ["asc", "desc"] ),
        direction: PropTypes.oneOf( ["asc", "desc"] ),
    } ),
    enableColumnFilters: PropTypes.bool,
    containerStyles: PropTypes.object,
    headerBg: PropTypes.oneOfType( [PropTypes.string, PropTypes.object] ),
};

/*
Example usage:
<CustomTable columns={columns} data={data} pageSize={10} initialSort={{ field: 'name', direction: 'asc' }} />

*/